import React, { useState, useEffect, useCallback, useRef } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import RXService from '@Services/RxService';
import { HEATMAP_OBSERVABLE_TOPICS, HEATMAP_SPLITTER_CHANGE_STATES } from '@Pages/Heatmap/constants';

const MIN_VALID_POSITION_WITH_STRUCTURE = 132;
const MIN_VALID_POSITION = 200;

const setElementLeftOffset = (el, leftOffset) => {
  // eslint-disable-next-line no-param-reassign
  el.style.left = `${leftOffset}px`;
};

function VerticalSplitter({ showStructure }) {
  const backdropRef = useRef();
  const splitterRef = useRef();

  const instanceRefs = useRef({
    touchedAt: null,
    initialX: null,
    lastValidX: null,
    moveFired: null,
  });

  const [isRowHeadersResizing, setIsRowHeadersResizing] = useState(false);
  const [splitterParams, setSplitterParams] = useState({});

  const mouseMoveHandler = useCallback(
    (e) => {
      if (!instanceRefs.current.moveFired) {
        RXService.next(HEATMAP_OBSERVABLE_TOPICS.verticalSplitter, HEATMAP_SPLITTER_CHANGE_STATES.moved);
        instanceRefs.current.moveFired = true;
      }

      const minValidPosition = showStructure ? MIN_VALID_POSITION_WITH_STRUCTURE : MIN_VALID_POSITION;
      const x = e.screenX - instanceRefs.current.touchedAt;
      const splitterWidth = splitterParams.width;
      const isValidPosition = x + splitterWidth > minValidPosition && x + splitterWidth < backdropRef.current.offsetWidth / 2;

      if (isValidPosition) {
        instanceRefs.current.lastValidX = e.screenX;
        setElementLeftOffset(splitterRef.current, x);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [splitterParams]
  );

  const mouseUpHandler = useCallback(() => {
    RXService.next(HEATMAP_OBSERVABLE_TOPICS.verticalSplitter, HEATMAP_SPLITTER_CHANGE_STATES.released, {
      widthDelta: instanceRefs.current.lastValidX - instanceRefs.current.initialX,
    });

    setIsRowHeadersResizing(false);
  }, []);

  const verticalSplitterChanged = useCallback((state, stateParams) => {
    if (state === HEATMAP_SPLITTER_CHANGE_STATES.activated) {
      instanceRefs.current = {
        // coordinate inside of splitter where mouse hit
        touchedAt: stateParams.eventParams.screenX - stateParams.splitterParams.left,
        initialX: stateParams.eventParams.screenX,
        lastValidX: stateParams.eventParams.screenX,
        moveFired: false,
      };

      setIsRowHeadersResizing(true);
      setSplitterParams(stateParams.splitterParams);
    }
  }, []);

  useEffect(() => {
    const rxSubscriptions = [RXService.subscribe(HEATMAP_OBSERVABLE_TOPICS.verticalSplitter, verticalSplitterChanged)];
    return () => {
      rxSubscriptions.forEach((rxSubscription) => rxSubscription.unsubscribe());
    };
  }, [verticalSplitterChanged]);

  useEffect(() => {
    if (isRowHeadersResizing) {
      window.addEventListener('mousemove', mouseMoveHandler, false);
      window.addEventListener('mouseup', mouseUpHandler, false);
    }
    return () => {
      if (isRowHeadersResizing) {
        window.removeEventListener('mousemove', mouseMoveHandler, false);
        window.removeEventListener('mouseup', mouseUpHandler, false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRowHeadersResizing]);

  const splitterStyles = {
    width: splitterParams.width,
    left: splitterParams.left,
  };

  const splitterInnerStyles = {
    width: splitterParams.width,
    height: splitterParams.height,
    top: splitterParams.top,
  };

  return (
    <div className={cn('heatmap__splitter-backdrop heatmap__backdrop-vertical', { 'd-none': !isRowHeadersResizing })} ref={backdropRef}>
      <div className="heatmap__resize-splitter-vertical" ref={splitterRef} style={splitterStyles}>
        <div className="heatmap__resize-splitter-vertical__inner" style={splitterInnerStyles} />
      </div>
    </div>
  );
}

VerticalSplitter.propTypes = {
  showStructure: PropTypes.bool.isRequired,
};

export default VerticalSplitter;
